/* eslint-disable @angular-eslint/component-selector */
import {
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@/layout/service/layout.service';
import { AppBreadcrumb } from './app.breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SHARED_IMPORTS } from '../../utils/other/standalone-import';
import { LanguageSwitcherComponent } from '@/components/misc/language-switcher/language-switcher.component';
import { AvatarModule } from 'primeng/avatar';
import { AuthService } from '@/services/auth/auth.service';
import { User } from '@sentry/angular';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
@Component({
  selector: '[app-topbar]',
  standalone: true,
  imports: [
    SHARED_IMPORTS,
    RouterModule,
    CommonModule,
    StyleClassModule,
    AppBreadcrumb,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    AvatarModule,
    LanguageSwitcherComponent
  ],
  template: `<div
    [ngClass]="showHeader ? 'layout-topbar card p-2 mb-4' : 'layout-topbar'">
    <div class="topbar-start">
      <button
        #menubutton
        type="button"
        class="topbar-menubutton p-link p-trigger"
        (click)="onMenuButtonClick()">
        <i class="pi pi-bars"></i>
      </button>
      <nav app-breadcrumb class="topbar-breadcrumb"></nav>
    </div>

    <div class="topbar-end flex">
      <ul class="topbar-menu">
        <li class="topbar-language">
          <app-language-switcher></app-language-switcher>
        </li>
        <li class="ml-3 mr-3">
          <button
            pButton
            class="p-button-icon-only transition-all duration-300"
            (click)="toggleDarkMode()"
            [icon]="darkTheme() ? 'pi pi-sun' : 'pi pi-moon'"
            [style]="{
              'font-size': '1.5rem',
              'border-radius': '50%',
              width: '2rem',
              height: '2rem',
              border: 'none',
              'box-shadow': darkTheme()
                ? 'none'
                : 'inset 0 0 6px rgba(255, 255, 255, 0.4)',
              'background-color': darkTheme() ? '#facc15' : '#1e293b',
              color: darkTheme() ? '#1e293b' : '#f8fafc'
            }"></button>
        </li>
        <li class="topbar-profile">
          <p-avatar
            *ngIf="!user?.['profilePictureId']"
            (click)="onProfileButtonClick()"
            [label]="initials"
            shape="circle"></p-avatar>

          <p-avatar
            *ngIf="user?.['profilePictureId']"
            (click)="onProfileButtonClick()"
            [image]="imgUrl"
            shape="circle"
            size="normal"
            [style]="{ 'background-color': 'transparent' }"></p-avatar>
        </li>
      </ul>
      <ul class="topbar-menu"></ul>
    </div>
  </div> `
})
export class AppTopbar implements OnInit, OnDestroy {
  private layoutService = inject(LayoutService);
  private authService = inject(AuthService);

  @ViewChild('menubutton') menuButton: ElementRef | null = null;

  readonly showHeader = environment.header;

  user: User | null = null;

  userSubscription: Subscription | null = null;

  ngOnInit(): void {
    this.userSubscription = this.authService.user$.subscribe((user) => {
      if (user) {
        this.user = user;
      }
    });
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  onProfileButtonClick() {
    this.layoutService.showProfileSidebar();
  }

  toggleDarkMode() {
    this.executeDarkModeToggle();
  }

  onConfigButtonClick() {
    this.layoutService.showConfigSidebar();
  }

  executeDarkModeToggle() {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      darkTheme: !state.darkTheme
    }));
  }

  darkTheme = computed(() => this.layoutService.layoutConfig().darkTheme);

  get initials(): string {
    if (!this.user) {
      return '';
    }

    return `${this.user['firstname']?.charAt(0)}${this.user['lastname']?.charAt(0)}`;
  }

  get imgUrl(): string | undefined {
    if (!this.user) {
      return undefined;
    }

    return `${environment.apiUrl}/users/avatar/${this.user.id}?profile-picture=${this.user['profilePictureId']}`;
  }
}
